import { showToast } from '../../engine/toast';
import { t, tOr } from '../../engine/i18n/i18n';
import { fetchJoinableWorlds } from '../worldmap/world-api';
import type { WorldSummary } from '../worldmap/world-api';
import { createColony, fetchTeamColorPalette } from './colony-api';
import type { TeamColor } from './colony-api';

/**
 * The one-time gate shown instead of the normal tab shell when the caller
 * has no colony yet — there is otherwise no way to reach `POST /me/colony`
 * at all (every tab just degrades gracefully around "no colony", none of
 * them can create one). Visually mirrors core/auth's login/register screens
 * (`bg-auth-gradient`/`bg-auth-card`) since this is the same kind of
 * pre-game, full-screen gate, just one step later in onboarding.
 */

const DEFAULT_PRIMARY = 'crimson';
const DEFAULT_SECONDARY = 'steel';

function escapeHtml(value: string): string {
  const div = document.createElement('div');
  div.textContent = value;
  return div.innerHTML;
}

/** "3 weeks left" / "5 days left" — coarse on purpose, it's a picker hint. */
function timeLeftLabel(endsAt: string, now: number = Date.now()): string {
  const ms = new Date(endsAt).getTime() - now;
  if (!Number.isFinite(ms) || ms <= 0) return t('foundColony.endingSoon');
  const days = Math.floor(ms / 86_400_000);
  if (days >= 14) return t('foundColony.weeksLeft', { count: Math.floor(days / 7) });
  if (days >= 1) return t(days === 1 ? 'foundColony.daysLeft.one' : 'foundColony.daysLeft.other', { count: days });
  return t('foundColony.endingSoon');
}

export function renderFoundColonyScreen(root: HTMLElement, onFounded: (colonyName: string, colonyId: string) => void): () => void {
  root.innerHTML = `
    <div class="min-h-screen grid place-items-center px-5 py-10 bg-auth-gradient">
      <div class="w-full max-w-[420px] bg-auth-card border border-border rounded-lg p-8 shadow-lift text-center text-text-dim">
        ${t('foundColony.loading')}
      </div>
    </div>
  `;

  let disposed = false;
  let worlds: WorldSummary[] = [];
  let palette: TeamColor[] = [];
  let primaryColor = DEFAULT_PRIMARY;
  let secondaryColor = DEFAULT_SECONDARY;
  let submitting = false;
  /** Two steps: pick a world (always shown, even with one), then name + colours. */
  let step: 'world' | 'details' = 'world';
  let selectedWorldId: string | null = null;
  /** Hoisted so a re-render (submit state, Change world → back) doesn't wipe out whatever the player already typed. */
  let name = '';

  function swatchesHtml(slot: 'primary' | 'secondary', selected: string, disabled: string): string {
    return palette
      .map((c) => {
        const isSel = c.id === selected;
        const isBlocked = c.id === disabled;
        const ring = isSel ? 'ring-2 ring-gold-bright' : 'ring-1 ring-border';
        return `<button type="button"
          data-action="pick-color" data-slot="${slot}" data-color-id="${c.id}"
          ${isBlocked ? 'disabled' : ''}
          aria-pressed="${isSel}"
          title="${isBlocked ? t('foundColony.colourBlocked', { label: tOr(`term.color.${c.id}`, c.label) }) : tOr(`term.color.${c.id}`, c.label)}"
          class="min-w-11 min-h-11 p-1.5 rounded ${ring} ${isBlocked ? 'opacity-25 cursor-not-allowed' : 'hover:ring-gold'} transition-shadow touch-manipulation flex items-center justify-center">
          <span class="w-full h-full rounded-sm" style="background:${c.hex}" aria-hidden="true"></span>
          <span class="sr-only">${tOr(`term.color.${c.id}`, c.label)}</span>
        </button>`;
      })
      .join('');
  }

  function render(): void {
    if (worlds.length === 0) {
      root.innerHTML = `
        <div class="min-h-screen grid place-items-center px-5 py-10 bg-auth-gradient">
          <div class="w-full max-w-[420px] bg-auth-card border border-border rounded-lg p-8 shadow-lift text-center">
            <h1 class="mb-2 font-heading text-xl text-gold tracking-wide">${t('foundColony.noWorldTitle')}</h1>
            <p class="text-sm text-text-dim">${t('foundColony.noWorldBody')}</p>
          </div>
        </div>
      `;
      return;
    }

    if (step === 'world') {
      renderWorldStep();
      return;
    }
    const world = selectedWorld();
    root.innerHTML = `
      <div class="min-h-screen grid place-items-center px-5 py-10 bg-auth-gradient overflow-y-auto">
        <form class="w-full max-w-[420px] bg-auth-card border border-border rounded-lg p-8 shadow-lift" id="found-colony-form" novalidate>
          <h1 class="mb-2 font-heading text-2xl text-gold tracking-wide">${t('foundColony.title')}</h1>
          <p class="mb-2 text-sm text-text-dim">${t('foundColony.settling', { world: `<span class="text-text">${escapeHtml(world.name)}</span>` })}
            <button type="button" data-action="change-world" ${submitting ? 'disabled' : ''}
              class="min-h-11 px-1 text-gold underline underline-offset-2 touch-manipulation hover:text-gold-bright">${t('foundColony.changeWorld')}</button>
          </p>
          <p class="mb-6 text-sm text-text-dim">${t('foundColony.placement')}</p>
          <div id="found-colony-error" class="mb-3.5 px-3 py-2.5 bg-error/[0.08] border border-error/40 rounded text-error text-[13px]" hidden></div>
          <label class="block mb-4">
            <span class="block mb-1.5 text-[13px] text-text-dim">${t('foundColony.nameLabel')}</span>
            <input type="text" name="name" minlength="1" maxlength="64" required autofocus
              placeholder="${escapeHtml(t('foundColony.namePlaceholder'))}" value="${escapeHtml(name)}"
              class="w-full px-3 py-2.5 bg-surface text-text border border-border rounded outline-none transition-colors duration-[120ms] focus:border-gold focus:shadow-glow" />
          </label>
          <div class="mb-4">
            <div class="text-[13px] text-text-dim mb-1.5">${t('foundColony.primaryColour')}</div>
            <div class="flex flex-wrap gap-1.5" id="swatches-primary">${swatchesHtml('primary', primaryColor, secondaryColor)}</div>
          </div>
          <div class="mb-6">
            <div class="text-[13px] text-text-dim mb-1.5">${t('foundColony.secondaryColour')} <span class="opacity-70">${t('foundColony.secondaryHint')}</span></div>
            <div class="flex flex-wrap gap-1.5" id="swatches-secondary">${swatchesHtml('secondary', secondaryColor, primaryColor)}</div>
          </div>
          <button type="submit" ${submitting ? 'disabled' : ''}
            class="inline-block w-full min-h-11 p-3 bg-gold text-bg font-bold border-0 rounded transition-colors duration-[120ms] hover:bg-gold-bright disabled:bg-border disabled:text-text-dim disabled:cursor-not-allowed" id="found-colony-btn">
            ${submitting ? t('foundColony.submitting') : t('foundColony.submit')}
          </button>
        </form>
      </div>
    `;
  }

  function selectedWorld(): WorldSummary {
    return worlds.find((w) => w.id === selectedWorldId) ?? worlds[0];
  }

  function renderWorldStep(): void {
    const cards = worlds
      .map((w) => {
        const isSel = w.id === selectedWorld().id;
        return `<button type="button" data-action="pick-world" data-world-id="${escapeHtml(w.id)}" aria-pressed="${isSel}"
          class="w-full min-h-11 text-left px-4 py-3 rounded border ${isSel ? 'border-gold bg-gold/[0.08]' : 'border-border hover:border-gold/60'} transition-colors touch-manipulation">
          <span class="flex items-baseline justify-between gap-3">
            <span class="font-heading text-text">${escapeHtml(w.name)}</span>
            <span class="text-[12px] text-text-dim whitespace-nowrap">${timeLeftLabel(w.endsAt)}</span>
          </span>
          <span class="block mt-1 text-[13px] text-text-dim">${t('foundColony.commanders', { current: w.playerCount, max: w.maxPlayers })}</span>
        </button>`;
      })
      .join('');
    root.innerHTML = `
      <div class="min-h-screen grid place-items-center px-5 py-10 bg-auth-gradient overflow-y-auto">
        <div class="w-full max-w-[420px] bg-auth-card border border-border rounded-lg p-8 shadow-lift" id="world-picker">
          <h1 class="mb-2 font-heading text-2xl text-gold tracking-wide">${t('foundColony.chooseTitle')}</h1>
          <p class="mb-6 text-sm text-text-dim">${t('foundColony.chooseIntro')}</p>
          <div class="flex flex-col gap-2 mb-6" role="list">${cards}</div>
          <button type="button" data-action="confirm-world" id="world-continue-btn"
            class="inline-block w-full min-h-11 p-3 bg-gold text-bg font-bold border-0 rounded transition-colors duration-[120ms] hover:bg-gold-bright">
            ${t('foundColony.continue')}
          </button>
        </div>
      </div>
    `;
  }

  /** Re-renders only the swatch rows, so a colour pick never rebuilds the form (and never steals focus from the name input on mobile). */
  function refreshSwatches(): void {
    const p = root.querySelector('#swatches-primary');
    const sec = root.querySelector('#swatches-secondary');
    if (p) p.innerHTML = swatchesHtml('primary', primaryColor, secondaryColor);
    if (sec) sec.innerHTML = swatchesHtml('secondary', secondaryColor, primaryColor);
  }

  function onInput(e: Event): void {
    const target = e.target as HTMLElement;
    if (target instanceof HTMLInputElement && target.name === 'name') name = target.value;
  }

  function onClick(e: MouseEvent): void {
    const target = e.target as HTMLElement;
    const worldBtn = target.closest('[data-action="pick-world"]') as HTMLElement | null;
    if (worldBtn) {
      selectedWorldId = worldBtn.dataset.worldId ?? null;
      render();
      return;
    }
    if (target.closest('[data-action="confirm-world"]')) {
      selectedWorldId = selectedWorld().id;
      step = 'details';
      render();
      return;
    }
    if (target.closest('[data-action="change-world"]')) {
      if (submitting) return;
      step = 'world';
      render();
      return;
    }
    const btn = target.closest('[data-action="pick-color"]') as HTMLElement | null;
    if (!btn) return;
    const slot = btn.dataset.slot as 'primary' | 'secondary';
    const colorId = btn.dataset.colorId!;
    if (slot === 'primary') {
      if (colorId === secondaryColor) return;
      primaryColor = colorId;
    } else {
      if (colorId === primaryColor) return;
      secondaryColor = colorId;
    }
    refreshSwatches();
  }

  async function onSubmit(e: Event): Promise<void> {
    e.preventDefault();
    if (submitting) return;
    const form = root.querySelector('#found-colony-form') as HTMLFormElement;
    // Read fresh from the DOM (not the hoisted `name`, which only exists to
    // survive onClick's render() and may lag a keystroke behind if the test/
    // caller sets .value without dispatching an input event).
    const trimmedName = (new FormData(form).get('name') as string | null)?.trim() ?? '';
    const errEl = root.querySelector('#found-colony-error') as HTMLDivElement | null;
    if (!trimmedName) {
      if (errEl) {
        errEl.textContent = t('foundColony.nameRequired');
        errEl.hidden = false;
      }
      return;
    }

    submitting = true;
    render();
    try {
      const colony = await createColony({ worldId: selectedWorld().id, name: trimmedName, primaryColor, secondaryColor });
      if (disposed) return;
      showToast(t('foundColony.founded', { name: trimmedName }), 'success');
      onFounded(trimmedName, colony.id);
    } catch (err) {
      if (disposed) return;
      submitting = false;
      render();
      const message = err instanceof Error ? err.message : t('foundColony.failed');
      const freshErrEl = root.querySelector('#found-colony-error') as HTMLDivElement | null;
      if (freshErrEl) {
        freshErrEl.textContent = message;
        freshErrEl.hidden = false;
      }
    }
  }

  root.addEventListener('click', onClick);
  root.addEventListener('input', onInput);
  root.addEventListener('submit', onSubmit);

  (async () => {
    const [worldsResult, paletteResult] = await Promise.allSettled([fetchJoinableWorlds(), fetchTeamColorPalette()]);
    if (disposed) return;
    if (worldsResult.status === 'fulfilled') worlds = worldsResult.value;
    if (paletteResult.status === 'fulfilled') palette = paletteResult.value;
    render();
  })();

  return () => {
    disposed = true;
    root.removeEventListener('click', onClick);
    root.removeEventListener('input', onInput);
    root.removeEventListener('submit', onSubmit);
  };
}
